import { memo, useCallback } from 'react';
import { useSelector } from '@xstate/store/react';
import { ALL_CONNECTOR_SIDES, MIN_TEXTBLOCK_WIDTH, MIN_TEXTBLOCK_HEIGHT } from '../../types';
import type { CanvasEntity } from '../../entityTypes';
import { getTextBlockData } from '../../entityTypes';
import { useCanvasStores } from '../../stores/CanvasContext';
import { useMergedDropTarget } from '../../hooks/useMergedDropTarget';
import { useLockedLayerIds } from '../../hooks/useLockedLayerIds';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { useEntityData } from '../../stores/EntityDataContext';
import { CanvasConnector } from '../../components/CanvasConnector';
import { CanvasResizeHandles } from '../../components/CanvasResizeHandles';
import { InlineRichEditor } from '../../components/InlineRichEditor';
import { startTiming } from '../../debugTimings';

export interface CanvasTextBlockProps {
  entity: CanvasEntity;
}

function CanvasTextBlockInner({ entity }: CanvasTextBlockProps) {
  const t = startTiming('TextBlock');

  t.mark('useCanvasStores');
  const { store } = useCanvasStores();
  t.mark('useMergedDropTarget');
  const { mergedDropTargetId, mergedDropTargetSide } = useMergedDropTarget();
  t.mark('useEntityData');
  const { selectedEdgeConnectors, connectorHasEdge } = useEntityData();
  t.mark('useLockedLayerIds');
  const lockedLayerIds = useLockedLayerIds();

  t.mark('getData');
  const d = getTextBlockData(entity);

  t.mark('useSelector-selected');
  const isSelected = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds.includes(entity.id));
  t.mark('useSelector-editing');
  const isEditing = useSelector(canvasSelectionStore, s => s.context.editingElementId === entity.id);

  t.mark('derived');
  const isLocked = lockedLayerIds.has(entity.layerId);
  const isDropTarget = mergedDropTargetId === entity.id;
  const dropTargetSide = isDropTarget ? mergedDropTargetSide : null;

  t.mark('useCallbacks');
  const handleResize = useCallback((x: number, y: number, width: number, height: number) => {
    store.send({ type: 'entity.resize', entityId: entity.id, x, y, width, height });
  }, [store, entity.id]);

  t.mark('jsx');
  t.done();

  return (
    <div
      data-canvas-entity
      data-entity-id={entity.id}
      data-entity-kind={entity.kind}
      data-canvas-textblock={entity.id}
      style={{
        position: 'absolute',
        left: entity.x,
        top: entity.y,
        width: entity.width,
        height: entity.height,
      }}
      className={`select-none ${isSelected ? 'ring-2 ring-blue-400' : ''} ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      <style>{`
[data-canvas-textblock] .tiptap-readonly p {
  margin: 0; padding: 0;
}
[data-canvas-textblock] .tiptap-readonly p + p {
  margin-top: 2px;
}
      `}</style>
      <div
        className="w-full h-full rounded-md overflow-hidden flex items-center justify-center cursor-grab active:cursor-grabbing"
        style={{
          backgroundColor: d.backgroundColor,
          border: d.borderColor
            ? `${d.borderWidth ?? 1}px solid ${d.borderColor}`
            : '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <div
          className="w-full h-full px-2 py-1 overflow-hidden"
          style={{
            color: d.textColor,
            fontSize: d.fontSize,
            fontWeight: d.fontWeight,
            textAlign: d.textAlign,
            display: 'flex',
            alignItems: 'center',
            justifyContent: d.textAlign === 'center' ? 'center' : d.textAlign === 'right' ? 'flex-end' : 'flex-start',
          }}
        >
          {isEditing ? (
            <InlineRichEditor
              entityId={entity.id}
              html={d.html}
              text={d.text}
              onSave={(html, text) => {
                store.send({ type: 'textblock.updateData', entityId: entity.id, dataChanges: { html, text } });
                canvasSelectionStore.send({ type: 'editing.stop' });
              }}
              onCancel={() => canvasSelectionStore.send({ type: 'editing.stop' })}
              className="w-full h-full"
            />
          ) : d.html ? (
            <div className="tiptap-readonly whitespace-pre-wrap break-words w-full" dangerouslySetInnerHTML={{ __html: d.html }} />
          ) : (
            <span className="whitespace-pre-wrap break-words">{d.text}</span>
          )}
        </div>
      </div>

      {/* Resize handles when selected */}
      {isSelected && !isLocked && (
        <CanvasResizeHandles
          x={entity.x}
          y={entity.y}
          width={entity.width}
          height={entity.height}
          minWidth={MIN_TEXTBLOCK_WIDTH}
          minHeight={MIN_TEXTBLOCK_HEIGHT}
          onResize={handleResize}
        />
      )}

      {/* Connectors */}
      {entity.connectable && ALL_CONNECTOR_SIDES.map(side => {
        const key = `${entity.id}:${side}`;
        return (
          <CanvasConnector
            key={side}
            taskId={entity.id}

            entityWidth={entity.width}
            entityHeight={entity.height}
            side={side}
            isDropTarget={isDropTarget}
            dropTargetSide={dropTargetSide}
            isEdgeEndpoint={selectedEdgeConnectors.has(key)}
            hasEdge={connectorHasEdge.has(key)}
          />
        );
      })}
    </div>
  );
}

export const CanvasTextBlock = memo(CanvasTextBlockInner);
